import { motion } from 'framer-motion';
import type { Website } from '../data/websites';

type Props = {
  website: Website;
  index?: number;
};

function Thumbnail({ website }: { website: Website }) {
  if (website.thumbnail) {
    return (
      <img
        src={website.thumbnail}
        alt={`${website.title} preview`}
        className="aspect-[16/10] w-full object-cover"
        loading="lazy"
      />
    );
  }
  // Gradient placeholder with title initial
  const initial = website.title.charAt(0).toUpperCase();
  return (
    <div className="relative flex aspect-[16/10] w-full items-center justify-center overflow-hidden bg-gradient-to-br from-accent-500/25 via-teal-500/15 to-ink-900">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <span className="relative font-display text-4xl font-semibold text-white/60 md:text-6xl">
        {initial}
      </span>
    </div>
  );
}

export default function WebsiteCard({ website, index = 0 }: Props) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: 'easeOut' }}
    >
      <a
        href={website.url}
        target="_blank"
        rel="noreferrer"
        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] shadow-card transition hover:border-white/20 hover:bg-white/[0.05]"
      >
        <div className="overflow-hidden">
          <div className="transition duration-500 group-hover:scale-[1.03]">
            <Thumbnail website={website} />
          </div>
        </div>

        <div className="flex flex-1 flex-col p-6">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-accent-400">
              {website.category}
            </p>
            <span className="text-xs text-white/40">{website.year}</span>
          </div>
          <h3 className="mt-2 font-display text-xl font-semibold text-white">
            {website.title}
          </h3>
          <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-white/70">
            {website.summary}
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            {website.tags.slice(0, 4).map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 font-mono text-[11px] text-white/65"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-auto pt-6">
            <p className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-400">
              Visit site
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                className="h-3.5 w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14 5h5v5M19 5l-9 9M5 5h5M5 5v14h14v-5"
                />
              </svg>
            </p>
          </div>
        </div>
      </a>
    </motion.article>
  );
}
